import { FormField } from "@/components/elements/FormField";
import { InnerBox } from "@/components/elements/InnerBox";
import { Input } from "@/components/elements/Input";
import { Grid, Stack, Typography } from "@mui/material";
import { FC, useEffect, useMemo, useState } from "react";
import { TCompanyForm } from "@/features/company/types/CompanyForm";
import { useFormContext, useWatch } from "react-hook-form";
import { Select } from "@/components/elements/Select";
import { SelectMulti } from "@/components/elements/SelectMulti";
import { CompanyFormTitle } from "@/features/company/components/company-info/CompanyFormTitle";
import { InputDatePicker } from "@/components/elements/InputDatePicker";
import { Button } from "@/components/elements/Button";
import { ConfirmSaveModal } from "@/components/commons/ConfirmSaveModal";
import { useDialog } from "@/hooks/useDialog";
import { dayjs } from "@/utils/dayjs";
import { useGetCompanySpecificIndustryList } from "@/api/services/global/master";
import { useInitSetup } from "@/providers/InitSetupProvider";
import { USER_TYPE } from "@/types/enum";
import { useBusinessClassificationSync } from "@/features/company/hooks/useBusinessClassificationSync";

type TCompanyBasicInfoProps = {
  readonly?: boolean;
};

export const CompanyBasicInfo: FC<TCompanyBasicInfoProps> = ({ readonly }) => {
  const { userType } = useInitSetup();
  const isCompanyUser = userType === USER_TYPE.COMPANY_USER;
  const methods = useFormContext<TCompanyForm>();
  const { control, setValue, trigger, formState: { errors } } = methods;
  const {
    companySpecificIndustryOptions,
    isLoading: isLoadingIndustry,
  } = useGetCompanySpecificIndustryList();
  const confirmDialog = useDialog();
  const [pendingDateField, setPendingDateField] = useState<"startDate" | "endDate" | null>(null);

  const [industryId1, industryId2, industryId3] = useWatch({
    control,
    name: ["companySpecificIndustryId1", "companySpecificIndustryId2", "companySpecificIndustryId3"],
  });
  const [businessClassificationIds1, businessClassificationIds2, businessClassificationIds3] = useWatch({
    control,
    name: [
      "companySpecificBusinessClassificationIds1",
      "companySpecificBusinessClassificationIds2",
      "companySpecificBusinessClassificationIds3",
    ],
  });

  const startDateWatchField = useWatch({ control, name: "startDate" });
  const endDateWatchField = useWatch({ control, name: "endDate" });

  // When startDate changes and endDate is currently in an error state, trigger re-validate endDate
  useEffect(() => {
    if (errors?.endDate) {
      trigger("endDate");
    }
  }, [startDateWatchField]);

  // When endDate changes and startDate is currently in an error state, trigger re-validate startDate
  useEffect(() => {
    if (errors?.startDate) {
      trigger("startDate");
    }
  }, [endDateWatchField]);

  // Industry options for each select, excluding the values selected by the other two slots (no duplicates).
  // Coerce with Number() because on the edit page the prefilled ids arrive as strings while option values
  // are numbers — a strict !== would never match and the duplicate would still show.
  const isSameIndustry = (optionValue: number, selectedId: number | null | undefined) =>
    selectedId != null && Number(optionValue) === Number(selectedId);
  const optionsForIndustryId1 = useMemo(
    () => companySpecificIndustryOptions.filter((opt) => !isSameIndustry(opt.value, industryId2) && !isSameIndustry(opt.value, industryId3)),
    [companySpecificIndustryOptions, industryId2, industryId3],
  );
  const optionsForIndustryId2 = useMemo(
    () => companySpecificIndustryOptions.filter((opt) => !isSameIndustry(opt.value, industryId1) && !isSameIndustry(opt.value, industryId3)),
    [companySpecificIndustryOptions, industryId1, industryId3],
  );
  const optionsForIndustryId3 = useMemo(
    () => companySpecificIndustryOptions.filter((opt) => !isSameIndustry(opt.value, industryId1) && !isSameIndustry(opt.value, industryId2)),
    [companySpecificIndustryOptions, industryId1, industryId2],
  );

  // 業務区分 options/auto-select/readonly are derived per row from the industry selected in that row
  const {
    options: businessClassificationOptions1,
    disabled: disabledBusinessClassification1,
    readonly: readonlyBusinessClassification1,
  } = useBusinessClassificationSync(industryId1, "companySpecificBusinessClassificationIds1", [...(businessClassificationIds2 ?? []), ...(businessClassificationIds3 ?? [])]);
  const {
    options: businessClassificationOptions2,
    disabled: disabledBusinessClassification2,
    readonly: readonlyBusinessClassification2,
  } = useBusinessClassificationSync(industryId2, "companySpecificBusinessClassificationIds2", [...(businessClassificationIds1 ?? []), ...(businessClassificationIds3 ?? [])]);
  const {
    options: businessClassificationOptions3,
    disabled: disabledBusinessClassification3,
    readonly: readonlyBusinessClassification3,
  } = useBusinessClassificationSync(industryId3, "companySpecificBusinessClassificationIds3", [...(businessClassificationIds1 ?? []), ...(businessClassificationIds2 ?? [])]);

  const handleSetToday = (field: "startDate" | "endDate") => {
    const currentValue = field === "startDate" ? startDateWatchField : endDateWatchField;
    if (currentValue) {
      setPendingDateField(field);
      confirmDialog.show();
    } else {
      setValue(field, dayjs().startOf("day"), { shouldValidate: true });
    }
  };

  const handleConfirmOverwrite = () => {
    if (pendingDateField) {
      setValue(pendingDateField, dayjs().startOf("day"), { shouldValidate: true });
      setPendingDateField(null);
    }
    confirmDialog.confirm();
  };

  const handleCancelOverwrite = () => {
    setPendingDateField(null);
    confirmDialog.cancel();
  };

  return (
    <Stack gap={2}>
      <InnerBox title={<CompanyFormTitle text="基本情報" />} showDivider={false}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormField control={control} name="managementNumber">
              <Input />
            </FormField>
          </Grid>
          <Grid item xs={6}>
            <FormField control={control} name="companyName">
              <Input readonly={readonly} />
            </FormField>
          </Grid>
          <Grid item xs={6}>
            <FormField control={control} name="companyKanaName">
              <Input readonly={readonly} />
            </FormField>
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <FormField control={control} name="companySpecificIndustryId1">
                  <Select placeholder="選択してください" loading={isLoadingIndustry} options={optionsForIndustryId1} />
                </FormField>
              </Grid>
              <Grid item xs={6}>
                <FormField control={control} name="companySpecificBusinessClassificationIds1">
                  <SelectMulti
                    placeholder="選択してください"
                    loading={isLoadingIndustry}
                    options={businessClassificationOptions1}
                    disabled={disabledBusinessClassification1}
                    readonly={readonlyBusinessClassification1}
                  />
                </FormField>
              </Grid>

              <Grid item xs={6}>
                <FormField control={control} name="companySpecificIndustryId2">
                  <Select placeholder="選択してください" loading={isLoadingIndustry} options={optionsForIndustryId2} />
                </FormField>
              </Grid>
              <Grid item xs={6}>
                <FormField control={control} name="companySpecificBusinessClassificationIds2">
                  <SelectMulti
                    placeholder="選択してください"
                    loading={isLoadingIndustry}
                    options={businessClassificationOptions2}
                    disabled={disabledBusinessClassification2}
                    readonly={readonlyBusinessClassification2}
                  />
                </FormField>
              </Grid>

              <Grid item xs={6}>
                <FormField control={control} name="companySpecificIndustryId3">
                  <Select placeholder="選択してください" loading={isLoadingIndustry} options={optionsForIndustryId3} />
                </FormField>
              </Grid>
              <Grid item xs={6}>
                <FormField control={control} name="companySpecificBusinessClassificationIds3">
                  <SelectMulti
                    placeholder="選択してください"
                    loading={isLoadingIndustry}
                    options={businessClassificationOptions3}
                    disabled={disabledBusinessClassification3}
                    readonly={readonlyBusinessClassification3}
                  />
                </FormField>
              </Grid>
            </Grid>
          </Grid>
          {!isCompanyUser && (
            <>
              <Grid item xs={6}>
                <Stack direction="row" alignItems="flex-start" gap={2}>
                  <Stack flex={1}>
                    <FormField control={control} name="startDate">
                      <InputDatePicker readonly={readonly} />
                    </FormField>
                  </Stack>
                </Stack>
              </Grid>
              <Grid item xs={3}>
                <Button variant="ghost" size="sm" onClick={() => handleSetToday("startDate")} sx={{ mt: "22px" }}>
                  今日の日付を入力
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Stack direction="row" alignItems="flex-start" gap={2}>
                  <Stack flex={1}>
                    <FormField control={control} name="endDate">
                      <InputDatePicker readonly={readonly} />
                    </FormField>
                  </Stack>
                </Stack>
              </Grid>
              <Grid item xs={3}>
                <Button variant="ghost" size="sm" onClick={() => handleSetToday("endDate")} sx={{ mt: "22px" }}>
                  今日の日付を入力
                </Button>
              </Grid>
            </>
          )}
        </Grid>
      </InnerBox>

      <ConfirmSaveModal open={confirmDialog.open} onClose={handleCancelOverwrite} onOk={handleConfirmOverwrite}>
        <Typography variant="subtitle1">現在の入力内容を上書きしますか？</Typography>
      </ConfirmSaveModal>
    </Stack>
  );
};
